import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Sparkles } from "lucide-react";

interface FocusedPlanWizardProps {
  isOpen: boolean;
  onClose: () => void;
  topicId: string;
  topicName: string;
  currentAccuracy: number;
  weakAreas: Array<{ tag: string; accuracy: number }>;
  questionsAttempted: number;
}

export function FocusedPlanWizard({
  isOpen,
  onClose,
  topicId,
  topicName,
  currentAccuracy,
  weakAreas,
  questionsAttempted
}: FocusedPlanWizardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [duration, setDuration] = useState("14");
  const [dailyHours, setDailyHours] = useState(1);
  const [targetAccuracy, setTargetAccuracy] = useState(Math.min(95, currentAccuracy + 15));
  const [selectedWeakAreas, setSelectedWeakAreas] = useState<string[]>(
    weakAreas.slice(0, 3).map(w => w.tag)
  );

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-focused-plan', {
        body: {
          topicId,
          topicName,
          currentAccuracy,
          targetAccuracy,
          weakTags: selectedWeakAreas,
          durationDays: parseInt(duration),
          hoursPerDay: dailyHours
        }
      });

      if (error) throw error;

      toast({
        title: "Study Plan Created! 🎯",
        description: `Your ${duration}-day focused plan for ${topicName} is ready.`,
      });

      onClose();
      navigate(`/study-plan?planId=${data.planId}`);
    } catch (error) {
      console.error('Error generating focused plan:', error);
      toast({
        title: "Failed to create study plan",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Focused Study Plan</DialogTitle>
          <DialogDescription>
            Generate an AI-powered study plan to improve your performance in {topicName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Current Performance Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Current Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Overall Accuracy:</span>
                <Badge variant={currentAccuracy >= 80 ? "default" : currentAccuracy >= 70 ? "secondary" : "destructive"}>
                  {currentAccuracy}%
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Questions Attempted:</span>
                <span className="font-medium">{questionsAttempted}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Weak Areas:</span>
                <span className="font-medium">{weakAreas.length} identified</span>
              </div>
            </CardContent>
          </Card>
          
          {/* Plan Configuration */}
          <div className="space-y-4">
            <div>
              <Label>How many days do you want to focus on this topic?</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">1 week (7 days)</SelectItem>
                  <SelectItem value="14">2 weeks (14 days)</SelectItem>
                  <SelectItem value="21">3 weeks (21 days)</SelectItem>
                  <SelectItem value="30">1 month (30 days)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Daily study time for this topic</Label>
              <Slider
                value={[dailyHours]}
                onValueChange={([val]) => setDailyHours(val)}
                min={0.5}
                max={4}
                step={0.5}
                className="mt-2"
              />
              <p className="text-sm text-muted-foreground mt-1">
                {dailyHours} {dailyHours === 1 ? 'hour' : 'hours'} per day
              </p>
            </div>
            
            <div>
              <Label>Target accuracy goal</Label>
              <Slider
                value={[targetAccuracy]}
                onValueChange={([val]) => setTargetAccuracy(val)}
                min={Math.min(currentAccuracy + 5, 95)}
                max={95}
                step={5}
                className="mt-2"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Aim for {targetAccuracy}% accuracy
              </p>
            </div>
            
            {/* Weak Areas Focus */}
            {weakAreas.length > 0 && (
              <div>
                <Label className="mb-2 block">Focus Areas (select all that apply)</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {weakAreas.map((area) => (
                    <div key={area.tag} className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedWeakAreas.includes(area.tag)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedWeakAreas([...selectedWeakAreas, area.tag]);
                          } else {
                            setSelectedWeakAreas(selectedWeakAreas.filter(t => t !== area.tag));
                          }
                        }}
                      />
                      <Label className="flex-1 cursor-pointer">
                        {area.tag}
                        <span className="text-muted-foreground ml-2">
                          ({area.accuracy}% accuracy)
                        </span>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Plan
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
